import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";

import { useQuoteDetailsPageLoadQueries } from "~/features/quote/hooks/use-quote-details-hooks.ts";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import type { ShipmentLineItem } from "~/shared/schemas/shipment-management-schema.ts";
import type {
	HandlingUnit,
	OnChange,
} from "~/shared/types/quote-details-types";
import {
	calculateItemDensity,
	getEstClass,
} from "~/shared/utils/commodity-utils.ts";

import { shipmentDraftAtom } from "../../../../state/shipment-edit-atoms.ts";
import { FreightCommodityFields } from "./commodity-fields.tsx";
import { mapHandlingUnitToLineItem } from "./map-handling-unit-to-line-item.ts";
import { mapLineItemToHandlingUnit } from "./map-line-item-to-handling-unit.ts";
import { validateFreightCommodity } from "./validate-freight-commodity.ts";

// Shipment freight is always imperial.
const MEASURING_TYPE = "LB/IN";
const SORT_AND_SEGREGATE = "Sort and Segregate";

type EditCommoditiesModalProps = {
	isOpen: boolean;
	onClose: () => void;
	commodity: ShipmentLineItem | null;
	onSave: (patch: Partial<ShipmentLineItem>) => void;
	mode?: "add" | "edit";
};

export const EditCommoditiesModal = ({
	isOpen,
	onClose,
	commodity,
	onSave,
	mode = "edit",
}: EditCommoditiesModalProps) => {
	const shipmentDraft = useAtomValue(shipmentDraftAtom);
	const { data } = useQuoteDetailsPageLoadQueries();
	const unitTypes = data?.unitTypes ?? [];
	// The shipment stores line items in a different shape than the editor's
	// `HandlingUnit`, so map on the way in.
	const [handlingUnit, setHandlingUnit] = useState<HandlingUnit | null>(() =>
		commodity ? mapLineItemToHandlingUnit(commodity, unitTypes) : null,
	);
	const [validationErrors, setValidationErrors] = useState<Set<string>>(
		() => new Set(),
	);

	// Re-seed the editable copy whenever a different commodity is opened.
	useEffect(() => {
		setHandlingUnit(
			commodity ? mapLineItemToHandlingUnit(commodity, unitTypes) : null,
		);
		setValidationErrors(new Set());
		// eslint-disable-next-line react-hooks/exhaustive-deps -- unitTypes only needs to be fresh, not re-trigger the reseed
	}, [commodity]);

	const clearFieldError = (fieldKey: string) => {
		setValidationErrors((prev) => {
			if (!prev.has(fieldKey)) {
				return prev;
			}
			const next = new Set(prev);
			next.delete(fieldKey);
			return next;
		});
	};

	const updateItem = (
		_item: HandlingUnit,
		field: keyof HandlingUnit,
		value: OnChange | null,
	) => {
		setHandlingUnit((prev) => {
			if (!prev) {
				return prev;
			}
			const updated = { ...prev, [field]: value };

			if (
				field === "quantity" ||
				field === "weight" ||
				field === "length" ||
				field === "width" ||
				field === "height"
			) {
				updated.density = calculateItemDensity(updated, MEASURING_TYPE);
				updated.estClass = getEstClass(updated.density);
			}

			return updated;
		});
	};

	if (!isOpen || !handlingUnit) {
		return null;
	}

	const sortAndSegregateSelected =
		(shipmentDraft?.pickup?.accessorials ?? []).includes(SORT_AND_SEGREGATE) ||
		(shipmentDraft?.delivery?.accessorials ?? []).includes(SORT_AND_SEGREGATE);
	const requirePieceCount = !!handlingUnit.isHazmat || sortAndSegregateSelected;

	const handleSave = () => {
		const errors = validateFreightCommodity(handlingUnit, {
			requirePieceCount,
		});
		if (errors.size > 0) {
			setValidationErrors(errors);
			return;
		}
		onSave(mapHandlingUnitToLineItem(handlingUnit));
		onClose();
	};

	return (
		<TraxModal
			cancelText={"Cancel"}
			confirmText={mode === "add" ? "Add" : "Update"}
			onCancel={onClose}
			onClose={onClose}
			onConfirm={handleSave}
			showConfirmationButtons={true}
			title={mode === "add" ? "Add Item" : "Edit Item"}
			type={"primary"}
		>
			<FreightCommodityFields
				handlingUnit={handlingUnit}
				idx={0}
				measuringType={MEASURING_TYPE}
				onClearFieldError={clearFieldError}
				requirePieceCount={requirePieceCount}
				showHazmatDetails={true}
				updateItem={updateItem}
				validationErrors={validationErrors}
			/>
		</TraxModal>
	);
};
